import { Component, OnInit } from '@angular/core';
import { formatDate } from '@angular/common';

import { UserInputsService } from '../services/user-inputs.service';
import { AirPollutionService } from '../services/air-pollution.service';
import { WeatherService } from '../services/weather.service';
import { HomeInfiltrationService } from '../services/home-infiltration.service';
import { ExposureCalculatorService } from '../services/exposure-calculator.service';
import { STACK_COEFFICIENTS, WIND_COEFFICIENTS } from '../models/data';
import { AirPollutionNetwork } from '../models/types';
import { WeatherConditions, Microenvironment } from '../models/user-inputs';
import { saveAs } from 'file-saver';
import { ChartType } from 'angular-google-charts';

@Component({
  selector: 'app-results',
  templateUrl: './results.component.html',
  styleUrls: ['./results.component.css']
})
export class ResultsComponent implements OnInit {
  
  numDays = 7;
  
  startDate = new Date();
  endDate = new Date();
  
  loadingLocation = true;
  loadingWeather = true;
  weatherStatus = 'loading';
  loadingAirPollution = true;
  airPollutionStatus = 'loading';
  resultsAvailable = false;
  error = '';
  
  latitude = 0;
  longitude = 0;
  
  pm25Exposures: number[] = [];
  ozoneExposures: number[] = [];
  pm25Doses: number[] = [];
  ozoneDoses: number[] = [];
  
  outdoorPM25Values: number[] = [];
  outdoorOzoneValues: number[] = [];
  
  weatherConditions: WeatherConditions[] = [];
  
  homeAirExchangeRateValues: number[] = [];
  homePM25InfiltrationFactors: number[] = [];
  homeOzoneInfiltrationFactors: number[] = [];
  homeIndoorPM25Levels: number[] = [];
  homeIndoorOzoneLevels: number[] = [];
  
  personalPM25ExposureFactors: number[] = [];
  personalOzoneExposureFactors: number[] = [];
  
  exposedTemperatures: number[] = [];
  exposedHumidities: number[] = [];
  exposedHeatIndexes: number[] = [];
  
  envPM25ExpPie = {type: ChartType.PieChart, data: [['', 0]], options: {title: '', enableInteractivity: false}, width: 600, height: 400};
  envOzoneExpPie = {type: ChartType.PieChart, data: [['', 0]], options: {title: '', enableInteractivity: false}, width: 600, height: 400};
  
  pm25ExposureColumn = {
    type: ChartType.ColumnChart,
    columns: ['% Change in PM2.5 Exposure'],
    data: [[new Date(), 0]],
    options: {title: '', colors: ['#3366CC'], legend: {position: 'none'}, enableInteractivity: false},
    width: 700, height: 400
  };
  
  ozoneExposureColumn = {
    type: ChartType.ColumnChart,
    columns: ['% Change in Ozone Exposure'],
    data: [[new Date(), 0]],
    options: {title: '', colors: ['#109618'], legend: {position: 'none'}, enableInteractivity: false},
    width: 700, height: 400
  };

  constructor(
    private userInputsService: UserInputsService,
    private airPollutionService: AirPollutionService,
    private weatherService: WeatherService,
    private homeInfiltrationService: HomeInfiltrationService,
    private exposureCalculatorService: ExposureCalculatorService
  ) { }

  ngOnInit(): void {
    this.endDate.setMinutes(0, 0, 0);
    this.startDate.setDate(this.startDate.getDate() - this.numDays);
    this.startDate.setMinutes(0, 0, 0);
    switch(this.userInputsService.locationOptions.mode) {
      case 'auto':
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition((position) => {
            this.loadingLocation = false;
            this.latitude = position.coords.latitude;
            this.longitude = position.coords.longitude;
            this.loadAirPollution();
            this.loadWeather();
          }, (error) => {
            console.log(error);
            this.error = "TracMyAir couldn't get your current location. Check your browser settings or go to About My Location and manually set your location.";
          });
        } else {
          console.log('Geolocation not available');
          this.error = "TracMyAir couldn't get your current location. Check your browser settings or go to About My Location and manually set your location.";
        }
        break;
    case 'address':
    case 'manual':
      this.loadingLocation = false;
      this.latitude = this.userInputsService.locationOptions.latitude;
      this.longitude = this.userInputsService.locationOptions.longitude;
      this.loadAirPollution();
      this.loadWeather();
      break;
    case 'data':
      this.loadingLocation = false;
      this.setNoLocationInputs();
      break;
    }
  }
  
  loadAirPollution(): void {
    this.airPollutionService.fetch24HourAverages(this.latitude, this.longitude, AirPollutionNetwork.airNow, this.startDate, this.endDate, this.numDays)
      .subscribe(result => {
      console.log(result);
      for (const data of result) {
        this.outdoorPM25Values.push(data.pm25);
        this.outdoorOzoneValues.push(data.ozone);
      }
      this.loadingAirPollution = false;
      this.airPollutionStatus = 'ready';
      this.doCalculations();
    }, error => {
      this.airPollutionStatus = 'failed';
      if (error == 'No monitors') {
        this.error += "TracMyAir couldn't locate any air monitors for your location.<br>";
      } else {
        this.error += "TracMyAir couldn't load enough air monitor data for your location. Please try again later.<br>";
      }
    });
  }
  
  loadWeather(): void {
    this.weatherService.fetch24HourAverages(this.latitude, this.longitude, this.startDate, this.endDate, this.numDays)
      .subscribe(result => {
      console.log(result);
      for (const data of result) {
        this.weatherConditions.push({
          temperature: data.temperature,
          windSpeed: data.windSpeed,
          humidity: data.humidity
        });
      }
      this.loadingWeather = false;
      this.weatherStatus = 'ready';
      this.doCalculations();
    }, error => {
      this.weatherStatus = 'failed';
      if (error == 'No stations') {
        this.error += "TracMyAir couldn't locate any weather stations for your location.<br>";
      } else {
        this.error += "TracMyAir couldn't load enough weather data for your location. Please try again later.<br>";
      }
    });
  }
    
  doCalculations(): void {
    if (this.loadingWeather) return;
    if (this.loadingAirPollution) return;
    
    for (let dayNum = 0; dayNum < this.numDays; dayNum++) {
      const homeAirExchangeRateValue = this.homeInfiltrationService.homeAirExchangeRate(
        this.userInputsService.homeCharacteristics,
        this.userInputsService.homeOperatingConditions,
        this.weatherConditions[dayNum]);
      this.homeAirExchangeRateValues.push(homeAirExchangeRateValue);
    
      const homePM25InfiltrationFactor = this.homeInfiltrationService.homeInfiltrationFactor('PM2.5',
        homeAirExchangeRateValue,
        this.userInputsService.homeCharacteristics,
        this.userInputsService.homeOperatingConditions,
        this.userInputsService.infiltrationParameters);
      this.homePM25InfiltrationFactors.push(homePM25InfiltrationFactor);
    
      const homeOzoneInfiltrationFactor = this.homeInfiltrationService.homeInfiltrationFactor('Ozone',
        homeAirExchangeRateValue,
        this.userInputsService.homeCharacteristics,
        this.userInputsService.homeOperatingConditions,
        this.userInputsService.infiltrationParameters);
      this.homeOzoneInfiltrationFactors.push(homeOzoneInfiltrationFactor);
      
      this.homeIndoorPM25Levels.push(this.outdoorPM25Values[dayNum] * homePM25InfiltrationFactor);
      this.homeIndoorOzoneLevels.push(this.outdoorOzoneValues[dayNum] * homeOzoneInfiltrationFactor);
    
      const personalPM25ExposureFactor = this.exposureCalculatorService.personalExposureFactor('PM2.5',
        homePM25InfiltrationFactor,
        this.userInputsService.userActivity,
        this.userInputsService.infiltrationParameters);
      this.personalPM25ExposureFactors.push(personalPM25ExposureFactor);
      const personalOzoneExposureFactor = this.exposureCalculatorService.personalExposureFactor('Ozone',
        homeOzoneInfiltrationFactor,
        this.userInputsService.userActivity,
        this.userInputsService.infiltrationParameters);
      this.personalOzoneExposureFactors.push(personalOzoneExposureFactor);
    
      this.pm25Exposures.push(this.outdoorPM25Values[dayNum] * personalPM25ExposureFactor);
      this.ozoneExposures.push(this.outdoorOzoneValues[dayNum] * personalOzoneExposureFactor);
      
      const pm25Dose = this.exposureCalculatorService.dose('PM2.5',
        this.outdoorPM25Values[dayNum],
        this.userInputsService.userCharacteristics,
        this.userInputsService.userActivity,
        this.userInputsService.userExercise,
        homePM25InfiltrationFactor,
        this.userInputsService.infiltrationParameters);
      this.pm25Doses.push(pm25Dose);
      
      const outdoorOzoneInUg = this.outdoorOzoneValues[dayNum] * 1.97;
      const ozoneDose = this.exposureCalculatorService.dose('Ozone',
        outdoorOzoneInUg,
        this.userInputsService.userCharacteristics,
        this.userInputsService.userActivity,
        this.userInputsService.userExercise,
        homeOzoneInfiltrationFactor,
        this.userInputsService.infiltrationParameters);
      this.ozoneDoses.push(ozoneDose);
    
      let exposedTemperature = 0;
      let exposedHumidity = 0;
      let exposedHeatIndex = 0;
      [exposedTemperature, exposedHumidity, exposedHeatIndex] = 
        this.exposureCalculatorService.exposedConditions(
          this.userInputsService.userActivity,
          this.weatherConditions[dayNum].temperature, this.weatherConditions[dayNum].humidity,
          this.userInputsService.otherConditions,
          this.userInputsService.homeOperatingConditions);
      this.exposedTemperatures.push(exposedTemperature);
      this.exposedHumidities.push(exposedHumidity);
      this.exposedHeatIndexes.push(exposedHeatIndex);
    }
    
    this.buildCharts();
    
    this.resultsAvailable = true;
  }
  
  setNoLocationInputs(): void {
    for (let dayNum = 0; dayNum < this.numDays; dayNum++) {
      this.outdoorPM25Values.push(this.userInputsService.airPollution.outdoorPM25);
      this.outdoorOzoneValues.push(this.userInputsService.airPollution.outdoorOzone);
      this.weatherConditions.push(this.userInputsService.weatherConditions);
    }
    
    this.loadingWeather = false;
    this.weatherStatus = 'ready';
    this.loadingAirPollution = false;
    this.airPollutionStatus = 'ready';
    
    this.doCalculations();
  }
  
  formatData(descr: string, units: string, value: string): string {
    const values = [
      '"' + descr + '"',
      '"' + units + '"',
      value
    ];
    return values.join(',') + '\n';
  }
  
  formatData2(descr: string, units: string, values: string []): string {
    let output = [
      '"' + descr + '"',
      '"' + units + '"'
    ].concat(values);
    return output.join(',') + '\n';
  }
  
  download(): void {
    // headers
    let values = [];
    for (let dayNum = 0; dayNum < this.numDays; dayNum++) {
      values.push(`"day ${dayNum + 1} value"`);
    }
    let output = this.formatData2('description', 'units', values);
    
    // start and end dates
    values = [];
    let dataDate = new Date(this.startDate);
    for (let dayNum = 0; dayNum < this.numDays; dayNum++) {
      values.push(`"${formatDate(dataDate, 'short', 'en-US')}"`)
      dataDate.setDate(dataDate.getDate() + 1);
    }
    output += this.formatData2('Start', '', values);
    
    values = [];
    dataDate = new Date(this.startDate);
    dataDate.setDate(dataDate.getDate() + 1);
    for (let dayNum = 0; dayNum < this.numDays; dayNum++) {
      values.push(`"${formatDate(dataDate, 'short', 'en-US')}"`)
      dataDate.setDate(dataDate.getDate() + 1);
    }
    output += this.formatData2('End', '', values);
    
    // exposures
    values = [];
    for (let dayNum = (this.numDays - 1); dayNum >= 0; dayNum--) {
      values.push(this.pm25Exposures[dayNum].toFixed(1));
    }
    output += this.formatData2(`PM2.5 exposure`, 'ug/m3', values);
    
    values = [];
    for (let dayNum = (this.numDays - 1); dayNum >= 0; dayNum--) {
      values.push(this.ozoneExposures[dayNum].toFixed(2));
    }
    output += this.formatData2('Ozone exposure', 'ppb', values);
    
    // doses
    values = [];
    for (let dayNum = (this.numDays - 1); dayNum >= 0; dayNum--) {
      values.push(this.pm25Doses[dayNum].toFixed(1));
    }
    output += this.formatData2(`PM2.5 dose per body surface area`, 'ug/m2', values);
    
    values = [];
    for (let dayNum = (this.numDays - 1); dayNum >= 0; dayNum--) {
      values.push(this.ozoneDoses[dayNum].toFixed(1));
    }
    output += this.formatData2('Ozone dose per body surface area', 'ug/m2', values);
    
    // location
    values.fill(`"${this.latitude.toFixed(2)}, ${this.longitude.toFixed(2)}"`);
    output += this.formatData2('Location', '', values);
    
    // outdoor air monitor values
    values = [];
    for (let dayNum = (this.numDays - 1); dayNum >= 0; dayNum--) {
      if (this.outdoorPM25Values[dayNum]) {
        values.push(this.outdoorPM25Values[dayNum].toFixed(1));
      } else {
        values.push('');
      }
    }
    output += this.formatData2(`Outdoor PM2.5`, 'ug/m3', values);
    
    values = [];
    for (let dayNum = (this.numDays - 1); dayNum >= 0; dayNum--) {
      if (this.outdoorOzoneValues[dayNum]) {
        values.push(this.outdoorOzoneValues[dayNum].toFixed(2));
      } else {
        values.push('');
      }
    }
    output += this.formatData2('Outdoor ozone', 'ppb', values);
    
    // outdoor weather conditions
    values = [];
    for (let dayNum = (this.numDays - 1); dayNum >= 0; dayNum--) {
      values.push(this.weatherConditions[dayNum].temperature.toFixed(1));
    }
    output += this.formatData2('Outdoor temperature', 'C', values);
    
    values = [];
    for (let dayNum = (this.numDays - 1); dayNum >= 0; dayNum--) {
      values.push(this.weatherConditions[dayNum].windSpeed.toFixed(1));
    }
    output += this.formatData2('Outdoor wind speed', 'km/hr', values);
    
    values = [];
    for (let dayNum = (this.numDays - 1); dayNum >= 0; dayNum--) {
      values.push(this.weatherConditions[dayNum].humidity.toFixed(1));
    }
    output += this.formatData2('Outdoor humidity', '%', values);
    
    // home indoor values
    values = [];
    for (let dayNum = (this.numDays - 1); dayNum >= 0; dayNum--) {
      values.push(this.homeAirExchangeRateValues[dayNum].toFixed(2));
    }
    output += this.formatData2('Home air exchange rate', '/hr', values);
    
    values = [];
    for (let dayNum = (this.numDays - 1); dayNum >= 0; dayNum--) {
      values.push(this.homePM25InfiltrationFactors[dayNum].toFixed(2));
    }
    output += this.formatData2('Home PM2.5 infiltration factor', '', values);
    
    values = [];
    for (let dayNum = (this.numDays - 1); dayNum >= 0; dayNum--) {
      values.push(this.homeOzoneInfiltrationFactors[dayNum].toFixed(2));
    }
    output += this.formatData2('Home ozone infiltration factor', '', values);
    
    values = [];
    for (let dayNum = (this.numDays - 1); dayNum >= 0; dayNum--) {
      values.push(this.homeIndoorPM25Levels[dayNum].toFixed(1));
    }
    output += this.formatData2(`Home indoor PM2.5 level`, 'ug/m3', values);
    
    values = [];
    for (let dayNum = (this.numDays - 1); dayNum >= 0; dayNum--) {
      values.push(this.homeIndoorOzoneLevels[dayNum].toFixed(2));
    }
    output += this.formatData2('Home indoor ozone level', 'ppb', values);
    
    // personal exposure factors
    values = [];
    for (let dayNum = (this.numDays - 1); dayNum >= 0; dayNum--) {
      values.push(this.personalPM25ExposureFactors[dayNum].toFixed(2));
    }
    output += this.formatData2('Personal PM2.5 exposure factor', '', values);
    
    values = [];
    for (let dayNum = (this.numDays - 1); dayNum >= 0; dayNum--) {
      values.push(this.personalOzoneExposureFactors[dayNum].toFixed(2));
    }
    output += this.formatData2('Personal ozone exposure factor', '', values);
    
    const data = new Blob([output], { type: 'text/plain;charset=utf-8' });
    saveAs(data, `tracmyair_exposure_results_${formatDate(this.startDate, 'yyyy-MM-dd_HH-mm', 'en-US', 'UTC')}Z.csv`);
  }
  
  convertCtoF(celsius: number): number {
    return (celsius * 9/5) + 32;
  }
  
  buildCharts(): void {
    // calculate start of last day
    let lastDate = new Date(this.endDate);
    lastDate.setDate(lastDate.getDate() - 1);
    
    const hoursInVehicles = this.userInputsService.userActivity[Microenvironment.vehicles];
    const hoursOutdoors = this.userInputsService.userActivity[Microenvironment.outdoors];
    const hoursIndoorsNotHome = this.userInputsService.userActivity[Microenvironment.other];
    const hoursIndoorsAtHome = 24 - (hoursInVehicles + hoursOutdoors + hoursIndoorsNotHome);
    
    const pm25Exposure = this.pm25Exposures[0];
    const ozoneExposure = this.ozoneExposures[0];
    
    this.envPM25ExpPie.options.title = `My PM2.5 Exposure for ${formatDate(lastDate, 'longDate', 'en-US')} by Location`;
    const outdoorPM25 = this.outdoorPM25Values[0];
    this.envPM25ExpPie.data = [
      ['Inside vehicles', +(100 * (outdoorPM25 * hoursInVehicles / 24 * this.userInputsService.infiltrationParameters.infiltrationFactorPM25Vehicles) / pm25Exposure).toFixed(1)],
      ['Outdoors', +(100 * (outdoorPM25 * hoursOutdoors / 24) / pm25Exposure).toFixed(1)],
      ['Indoors not at home', +(100 * (outdoorPM25 * hoursIndoorsNotHome / 24 * this.userInputsService.infiltrationParameters.infiltrationFactorPM25NotHomes) / pm25Exposure).toFixed(1)],
      ['Indoors at home', +(100 * (outdoorPM25 * hoursIndoorsAtHome / 24 * this.homePM25InfiltrationFactors[0]) / pm25Exposure).toFixed(1)]
    ];
    
    this.envOzoneExpPie.options.title = `My Ozone Exposure for ${formatDate(lastDate, 'longDate', 'en-US')} by Location`;
    const outdoorOzone = this.outdoorOzoneValues[0];
    this.envOzoneExpPie.data = [
      ['Inside vehicles', +(100 * (outdoorOzone * hoursInVehicles / 24 * this.userInputsService.infiltrationParameters.infiltrationFactorOzoneVehicles) / ozoneExposure).toFixed(1)],
      ['Outdoors', +(100 * (outdoorOzone * hoursOutdoors / 24) / ozoneExposure).toFixed(1)],
      ['Indoors not at home', +(100 * (outdoorOzone * hoursIndoorsNotHome / 24 * this.userInputsService.infiltrationParameters.infiltrationFactorOzoneNotHomes) / ozoneExposure).toFixed(1)],
      ['Indoors at home', +(100 * (outdoorOzone * hoursIndoorsAtHome / 24 * this.homeOzoneInfiltrationFactors[0]) / ozoneExposure).toFixed(1)]
    ];
    
    this.pm25ExposureColumn.options.title = `Changes in My Daily PM2.5 Exposure Relative to ${formatDate(lastDate, 'longDate', 'en-US')} (%)`;
    let dataDate = new Date(this.startDate);
    dataDate.setHours(0);
    this.pm25ExposureColumn.data = [];
    for (let dayNum = (this.numDays - 1); dayNum >= 0; dayNum--) {
      this.pm25ExposureColumn.data.push(
        [new Date(dataDate), +(100 * (this.pm25Exposures[dayNum] - pm25Exposure) / pm25Exposure).toFixed(1)]);
      dataDate.setDate(dataDate.getDate() + 1);
    }
    
    this.ozoneExposureColumn.options.title = `Changes in My Daily Ozone Exposure Relative to ${formatDate(lastDate, 'longDate', 'en-US')} (%)`;
    dataDate = new Date(this.startDate);
    dataDate.setHours(0);
    this.ozoneExposureColumn.data = [];
    for (let dayNum = (this.numDays - 1); dayNum >= 0; dayNum--) {
      this.ozoneExposureColumn.data.push(
        [new Date(dataDate), +(100 * (this.ozoneExposures[dayNum] - ozoneExposure) / ozoneExposure).toFixed(1)]);
      dataDate.setDate(dataDate.getDate() + 1);
    }
  }
}
